import React, {useMemo, useState} from "react";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {IconButton} from "@mui/material";
import {GridColDef} from "@mui/x-data-grid";
import {
  FilterOption,
  countActiveFilters,
  FilterSidebar,
  FiltersToggleButton,
  FilterValues,
  hasActiveFilters,
  LIST_HEADER_HEIGHT,
  ListDataGrid,
  ListFilter,
  ListPageHeader,
  matchesFilters,
  SearchInput,
  SidebarFilterMatchMode,
  Tooltip,
  sortedByLocale,
} from "../../ui/components";
import {AccountCountInfoTooltip} from "../../components/tooltips";
import {Box, Chip, Colors, Text} from "@bounce/shared/ui";
import {useLocalStorageState} from "@bounce/shared/hooks";
import {prettifyCurrencyRounded} from "@bounce/shared/helpers";
import {useGetAudiences} from "../../apis/requests/audiences/getAudiences";
import {useGetSegments} from "../../apis/requests/segments/getSegments";
import {segmentFilterOptions} from "../segments/segmentFilterOptions";
import {AUDIENCES_QUERY_KEY} from "../../apis/requests/queryKeys";
import {toQueryParams} from "../../apis/requests/queryParams";
import {AudienceRow as AudienceRowData} from "../../apis/requests/audiences/types";
import {useDebounce} from "../../hooks/useDebounce";
import {useFilterPanel} from "../../hooks/useFilterPanel";
import {reachUrls} from "../../layout/menu-items";
import {AudienceRecalculationBanner} from "../../components/AudienceRecalculationBanner";
import {TemplatesPerChannelDrawer} from "./TemplatesPerChannelDrawer";
import {sumTemplatesPerChannel} from "./templatesPerChannel";
import {SegmentNameChip} from "../segments/SegmentNameChip";

const AUDIENCES_FILTERS_STORAGE_KEY = "reach-audiences-filters";
const AUDIENCE_SEGMENTS_MATCH_MODE_STORAGE_KEY = "reach-audiences-audience-match-mode";
const CAMPAIGN_SEGMENTS_MATCH_MODE_STORAGE_KEY = "reach-audiences-campaign-match-mode";
const AUDIENCES_FILTER_PANEL_STORAGE_KEY = "reach-audiences-filter-panel-expanded";
const HEADER_CONTROL_HEIGHT = 40;

const ID_COLUMN_WIDTH = 100;

const DEFAULT_CAMPAIGN_FILTER: FilterOption = {value: "__default__", label: "Default", chipVariant: "inherit"};

const AudiencesPage = () => {
  const {data, isLoading, error} = useGetAudiences();
  const audiences = data ?? [];
  const {data: segments} = useGetSegments();
  const [filterValues, setFilterValues] = useLocalStorageState<FilterValues>(
    AUDIENCES_FILTERS_STORAGE_KEY,
    {},
    {encrypted: false}
  );
  const [audienceMatchMode, setAudienceMatchMode] = useLocalStorageState<SidebarFilterMatchMode>(
    AUDIENCE_SEGMENTS_MATCH_MODE_STORAGE_KEY,
    "any",
    {encrypted: false}
  );
  const [campaignMatchMode, setCampaignMatchMode] = useLocalStorageState<SidebarFilterMatchMode>(
    CAMPAIGN_SEGMENTS_MATCH_MODE_STORAGE_KEY,
    "any",
    {encrypted: false}
  );
  const filterPanel = useFilterPanel(AUDIENCES_FILTER_PANEL_STORAGE_KEY);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search);
  const [templatesDrawerAudience, setTemplatesDrawerAudience] = useState<AudienceRowData | null>(null);

  const getAudienceUrl = (row: AudienceRowData) =>
    `${reachUrls.audienceDetail}${toQueryParams("segment_ids", row.audience_segment_ids)}`;

  const audienceSegmentOptions = useMemo(
    () =>
      segmentFilterOptions(
        audiences.flatMap((row) => row.audience_segment_names),
        segments ?? []
      ),
    [audiences, segments]
  );
  const campaignSegmentOptions = useMemo(
    () =>
      segmentFilterOptions(
        audiences.flatMap((row) => row.campaign_segment_names),
        segments ?? []
      ),
    [audiences, segments]
  );

  const filters = useMemo<ListFilter<AudienceRowData>[]>(
    () => [
      {
        key: "audience_segments",
        placeholder: "Audience segments",
        options: audienceSegmentOptions,
        getRowValues: (row) => row.audience_segment_names,
        chipVariant: "secondary",
        matchMode: {value: audienceMatchMode, onChange: setAudienceMatchMode},
      },
      {
        key: "campaign_segments",
        placeholder: "Campaign segments",
        options: [DEFAULT_CAMPAIGN_FILTER, ...campaignSegmentOptions],
        getRowValues: (row) =>
          row.campaign_segment_names.length === 0 ? [DEFAULT_CAMPAIGN_FILTER.value] : row.campaign_segment_names,
        chipVariant: "inherit",
        matchMode: {value: campaignMatchMode, onChange: setCampaignMatchMode},
      },
    ],
    [
      audienceSegmentOptions,
      campaignSegmentOptions,
      audienceMatchMode,
      setAudienceMatchMode,
      campaignMatchMode,
      setCampaignMatchMode,
    ]
  );

  const filteredRows = useMemo(() => {
    const searchTerm = debouncedSearch.trim().toLowerCase();
    return audiences.filter(
      (row) => matchesFilters(row, filters, filterValues) && (searchTerm === "" || String(row.id).includes(searchTerm))
    );
  }, [audiences, filters, filterValues, debouncedSearch]);

  const columns = useMemo<GridColDef<AudienceRowData>[]>(
    () => [
      {
        field: "id",
        headerName: "ID",
        width: ID_COLUMN_WIDTH,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: ({row}) => (
          <Tooltip title={row.id} placement="top" disableHoverListener={!row.id}>
            <Box sx={{minWidth: 0, maxWidth: "100%"}}>
              <Text
                size={14}
                sx={{color: Colors.grey._700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}
              >
                {row.id ?? "—"}
              </Text>
            </Box>
          </Tooltip>
        ),
      },
      {
        field: "audience_segment_names",
        headerName: "Audiences",
        flex: 4,
        valueGetter: (_value, row) => sortedByLocale(row.audience_segment_names).join(", "),
        renderCell: ({row}) => (
          <Box sx={{display: "flex", flexWrap: "wrap", gap: 0.5, alignItems: "center", py: 1}}>
            {row.audience_segment_names.length === 0 ? (
              <Chip size_="small" variant_="inherit" label="Unassigned" />
            ) : (
              row.audience_segment_names.map((name, i) => (
                <SegmentNameChip
                  key={i}
                  name={name}
                  variant="secondary"
                />
              ))
            )}
          </Box>
        ),
      },
      {
        field: "account_count",
        headerName: "Accounts",
        flex: 1,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderHeader: () => (
          <Box sx={{display: "flex", alignItems: "center", gap: 0.5}}>
            <Text size={12} variant="inter600" sx={{color: Colors.grey._700}}>
              Accounts
            </Text>
            <AccountCountInfoTooltip />
          </Box>
        ),
        renderCell: ({row}) => (
          <Text size={14} sx={{color: Colors.grey._1000}}>
            {row.account_count.toLocaleString()}
          </Text>
        ),
      },
      {
        field: "total_balance",
        headerName: "Face value",
        flex: 1,
        filterable: false,
        disableColumnMenu: true,
        renderCell: ({row}) => (
          <Text size={14} sx={{color: Colors.grey._1000}}>
            {prettifyCurrencyRounded(row.total_balance)}
          </Text>
        ),
      },
      {
        field: "campaign_segment_names",
        headerName: "Campaign",
        flex: 3,
        sortable: false,
        disableColumnMenu: true,
        renderCell: ({row}) => (
          <Box sx={{display: "flex", flexWrap: "wrap", gap: 0.5, alignItems: "center", py: 1}}>
            {row.campaign_segment_names.length === 0 ? (
              <Chip size_="small" variant_="inherit" label="Default" />
            ) : (
              row.campaign_segment_names.map((name, i) => (
                <SegmentNameChip
                  key={i}
                  name={name}
                  variant="inherit"
                />
              ))
            )}
          </Box>
        ),
      },
      {
        field: "num_of_templates_per_channel",
        headerName: "Templates",
        flex: 1,
        sortable: false,
        filterable: false,
        valueGetter: (_value, row) => sumTemplatesPerChannel(row.num_of_templates_per_channel),
        renderCell: ({row}) => (
          <Box sx={{display: "flex", alignItems: "center", gap: 0.5}}>
            <Text size={14} sx={{color: Colors.grey._1000}}>
              {sumTemplatesPerChannel(row.num_of_templates_per_channel).toLocaleString()}
            </Text>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                setTemplatesDrawerAudience(row);
              }}
              sx={{color: Colors.grey._700}}
            >
              <ChevronRightIcon sx={{fontSize: 20}} />
            </IconButton>
          </Box>
        ),
      },
    ],
    []
  );

  return (
    <Box sx={{p: 4, display: "flex", flexDirection: "column", height: "100%"}}>
      <ListPageHeader title="Audiences">
        <FiltersToggleButton panel={filterPanel} activeCount={countActiveFilters(filterValues)} />
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="Search by audience ID"
          sx={{height: HEADER_CONTROL_HEIGHT}}
        />
      </ListPageHeader>
      <AudienceRecalculationBanner dataQueryKey={AUDIENCES_QUERY_KEY} />
      <Box sx={{display: "flex", flex: 1, minHeight: 0}}>
        <FilterSidebar filters={filters} values={filterValues} onChange={setFilterValues} panel={filterPanel} />
        <Box sx={{display: "flex", flexDirection: "column", flex: 1, minWidth: 0, minHeight: 0}}>
          {error ? (
            <Text size={14}>Failed to load audiences.</Text>
          ) : (
            <ListDataGrid
              rows={filteredRows}
              columns={columns}
              loading={isLoading}
              getRowId={(row) => row.id}
              getRowUrl={getAudienceUrl}
              pageSizeOptions={[10, 25, 50, 100]}
              disableColumnMenu={false}
              columnHeaderHeight={LIST_HEADER_HEIGHT}
              noRowsText={
                debouncedSearch || hasActiveFilters(filterValues)
                  ? "No audiences match your filters"
                  : "No audiences yet"
              }
            />
          )}
        </Box>
      </Box>
      <TemplatesPerChannelDrawer audience={templatesDrawerAudience} onClose={() => setTemplatesDrawerAudience(null)} />
    </Box>
  );
};

export default AudiencesPage;
